import React, { useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { State } from "../../app/store";
import useFacility from "../../utils/hooks/useFacility";
import { useCookieConsent } from "../../utils/cookieConsent/useCookieConsent";
import { useCookieBannerIframePosition } from "./useCookieBannerIframePosition";
import EAButton from "../../app/components/shared/Buttons/EAButton";
import "./CookieBanner.scss";
import CookieSettingsModal from "./CookieSettingsModal";

const CookieBanner: React.FC = () => {
  const { t } = useTranslation();
  const facilityId = useFacility();
  const configuration = useSelector((state: State) => state.config.configuration);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  const { shouldShowBanner, consentValue, resolvedTags, acceptAll, rejectOptional, updateConsent } = useCookieConsent(
    facilityId,
    configuration,
  );

  const positionStyle = useCookieBannerIframePosition(bannerRef);

  if (!configuration.cookieBarActive) return null;

  const privacyPolicyUrl = configuration.regulaminList?.find((r) => r.active)?.file ?? "";

  return ReactDOM.createPortal(
    <>
      {shouldShowBanner && positionStyle !== null && (
        <div
          ref={bannerRef}
          className="cookie-banner"
          style={positionStyle}
          role="region"
          aria-label={t("pliki_cookie")}
        >
          <h3 className="cookie-banner__title">{t("pliki_cookie")}</h3>
          <p className="cookie-banner__text">{t("cookie_banner_text")}</p>
          <div className="cookie-banner__actions">
            <EAButton
              title={t("cookie_accept_all")}
              compact
              onClick={() => {
                acceptAll();
              }}
            />
            <EAButton
              title={t("cookie_reject_optional")}
              variant="outlined"
              compact
              onClick={() => {
                rejectOptional();
              }}
            />
            <EAButton
              title={t("ustawienia")}
              variant="text"
              compact
              onClick={() => setIsSettingsOpen(true)}
            />
          </div>
        </div>
      )}

      <CookieSettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        onUpdateConsent={(optional: boolean) => {
          updateConsent(optional);
          setIsSettingsOpen(false);
        }}
        onRejectOptional={() => {
          rejectOptional();
          setIsSettingsOpen(false);
        }}
        resolvedTags={resolvedTags}
        privacyPolicyUrl={privacyPolicyUrl}
        facilityName={configuration.facilityName}
        currentConsent={consentValue}
      />
    </>,
    document.body,
  );
};

export default CookieBanner;
